import { PLAYER_COLORS, PLAYER_BORDER_COLORS } from "@/lib/api";

/**
 * SetupChoice — one-time modal presented at the start of Phase 1.
 *
 * "Place Manually" dismisses and lets the existing round-robin flow
 * take over. "Randomize Setup" fires the randomize endpoint and
 * jumps straight to Round 1.
 */
export default function SetupChoice({
  players,
  cubesPerPlayer,
  gridCols,
  gridRows,
  busy,
  onManual,
  onRandomize,
  canRandomize = true,
}) {
  return (
    <div
      data-testid="setup-choice-modal"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(43,38,32,0.55)" }}
    >
      <div
        className="w-full max-w-lg"
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--line)",
          borderRadius: 12,
          padding: 28,
          boxShadow: "var(--shadow-card)",
        }}
      >
        <div
          className="badge-text mb-2"
          style={{ color: "var(--ink-soft)" }}
        >
          Phase 1 · Setup
        </div>
        <h2
          style={{
            fontFamily: "Figtree, sans-serif",
            fontWeight: 800,
            fontSize: 28,
            color: "var(--ink)",
            letterSpacing: "-0.01em",
          }}
        >
          How should the starting cubes go down?
        </h2>
        <p
          className="mt-3"
          style={{ fontSize: 13, color: "var(--ink-soft)", lineHeight: 1.5 }}
        >
          {players.length} players · {cubesPerPlayer} cubes each on a{" "}
          {gridCols}×{gridRows} board. Pick a setup style — you can only
          choose once.
        </p>

        {/* Player line-up */}
        <div className="flex items-center gap-2 mt-4">
          {players.map((p) => (
            <div
              key={p.id}
              className="rounded-md h-8 w-8"
              title={p.name}
              style={{
                background: PLAYER_COLORS[p.color],
                border: `2px solid ${PLAYER_BORDER_COLORS[p.color]}`,
              }}
            />
          ))}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="setup-choice-manual"
            disabled={busy}
            onClick={onManual}
            className="text-left p-4 rounded-lg transition-transform"
            style={{
              background: "var(--bg-card)",
              border: "1px solid var(--line)",
              boxShadow: "var(--shadow-card)",
            }}
          >
            <div
              style={{
                fontFamily: "Figtree, sans-serif",
                fontWeight: 700,
                fontSize: 18,
                color: "var(--ink)",
              }}
            >
              Place Manually
            </div>
            <div
              className="mt-1"
              style={{ fontSize: 12, color: "var(--ink-soft)", lineHeight: 1.45 }}
            >
              In seat order, each player places one House Cube at a time
              until everyone has placed {cubesPerPlayer}.
            </div>
          </button>

          {canRandomize && (
          <button
            type="button"
            data-testid="setup-choice-randomize"
            disabled={busy}
            onClick={onRandomize}
            className="text-left p-4 rounded-lg transition-transform"
            style={{
              background: "var(--ink)",
              border: "1px solid var(--line)",
              color: "var(--bg-card)",
              boxShadow: "var(--shadow-card)",
            }}
          >
            <div
              style={{
                fontFamily: "Figtree, sans-serif",
                fontWeight: 700,
                fontSize: 18,
                color: "var(--bg-card)",
              }}
            >
              Randomize Setup
            </div>
            <div
              className="mt-1"
              style={{
                fontSize: 12,
                color: "rgba(253,251,245,0.75)",
                lineHeight: 1.45,
              }}
            >
              Auto-place all cubes on distinct random squares, deal starting
              hands, and jump straight to Round 1.
            </div>
          </button>
          )}
        </div>
      </div>
    </div>
  );
}
